import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  BanknotesIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { RootState, AppDispatch } from '../store/store';
import { createOrder } from '../store/slices/orderSlice';
import { clearCart } from '../store/slices/cartSlice';

const CheckoutPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { items, subtotal, taxes, total } = useSelector((state: RootState) => state.cart);
  const { isAuthenticated, user, guestUser } = useSelector((state: RootState) => state.auth);
  const { isLoading, error } = useSelector((state: RootState) => state.order);

  const currentUser = user || guestUser;

  const [paymentMethod, setPaymentMethod] = useState<'cash_on_delivery' | 'card'>('cash_on_delivery');
  const [deliveryInfo, setDeliveryInfo] = useState({
    name: user?.firstName || guestUser?.name || '',
    phone: user?.phone || guestUser?.phone || '',
    email: user?.email || guestUser?.email || '',
    address: '',
    city: '',
    postcode: '',
    specialInstructions: '',
  });

  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [isOrderPlaced, setIsOrderPlaced] = useState(false);

  useEffect(() => {
    // Only redirect to menu if cart is empty AND order hasn't been placed
    if (items.length === 0 && !isOrderPlaced) {
      navigate('/menu');
    }
  }, [items.length, navigate, isOrderPlaced]);

  const handleInputChange = (field: string, value: string) => {
    setDeliveryInfo(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user types
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};

    if (!deliveryInfo.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!deliveryInfo.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^(\+44\s?|0)(\d\s?){9,10}$/.test(deliveryInfo.phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid UK phone number (e.g., 07123 456789 or +44 7123 456789)';
    }

    if (!deliveryInfo.address.trim()) {
      errors.address = 'Delivery address is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = document.querySelector('.border-red-500');
      firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const orderData = {
      items: items.map(item => ({
        itemCode: item.itemCode,
        itemName: item.itemName,
        quantity: item.quantity,
        rate: item.rate,
        customizations: item.customizations,
        specialInstructions: item.specialInstructions,
      })),
      orderType: 'delivery' as const,
      deliveryAddress: {
        street: deliveryInfo.address,
        city: deliveryInfo.city,
        state: '', // UK doesn't use state
        zipCode: deliveryInfo.postcode,
      },
      paymentMethod: paymentMethod,
      specialInstructions: deliveryInfo.specialInstructions,
      // Always include customer info for both guest and authenticated users
      guestInfo: {
        name: deliveryInfo.name,
        phone: deliveryInfo.phone,
        email: deliveryInfo.email || (user?.email) || undefined,
      },
    };

    try {
      const result = await dispatch(createOrder(orderData));
      console.log('Order creation result:', result);

      if (createOrder.fulfilled.match(result)) {
        console.log('Order fulfilled, payload:', result.payload);

        // The API returns { success: true, message: "...", order: {...} }
        const responseData: any = result.payload;
        const order = responseData?.order;

        if (order) {
          // Set flag to prevent redirect to menu when cart clears
          setIsOrderPlaced(true);

          console.log('Navigating to thank-you with order:', order);

          // Navigate to Thank You page with order details
          navigate('/thank-you', {
            state: {
              order: order,
              orderNumber: order?.orderNumber || order?.id || 'N/A'
            },
            replace: true // Prevent going back to checkout
          });

          // Clear cart AFTER navigation
          setTimeout(() => {
            dispatch(clearCart());
          }, 100);
        } else {
          console.error('No order data in response:', responseData);
        }
      } else {
        console.error('Order creation not fulfilled:', result);
      }
    } catch (error) {
      console.error('Order creation failed:', error);
    }
  };

  const deliveryTime = new Date();
  deliveryTime.setMinutes(deliveryTime.getMinutes() + 45);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🛒 Checkout
          </h1>
          <p className="text-gray-600">
            Complete your order - Fast delivery in 30-45 minutes
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Guest vs Registered User */}
            {!isAuthenticated && (
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-blue-900">Quick Guest Checkout</h3>
                    <p className="text-sm text-blue-700">
                      No account needed, but consider signing up for faster future orders
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/register')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            )}

            {/* Delivery Information */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="w-5 h-5 mr-2" />
                Delivery Information
              </h2>

              <form onSubmit={handleSubmitOrder} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={deliveryInfo.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        validationErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Your full name"
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={deliveryInfo.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        validationErrors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="07123 456789 or +44 7123 456789"
                    />
                    {validationErrors.phone && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={deliveryInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="your@email.com (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={deliveryInfo.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      validationErrors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Street address, apartment, etc."
                  />
                  {validationErrors.address && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={deliveryInfo.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postcode
                    </label>
                    <input
                      type="text"
                      value={deliveryInfo.postcode}
                      onChange={(e) => handleInputChange('postcode', e.target.value.toUpperCase())}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="SW1A 1AA"
                      maxLength={8}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Instructions
                  </label>
                  <textarea
                    value={deliveryInfo.specialInstructions}
                    onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Delivery instructions, allergies, etc."
                  />
                </div>
              </form>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCardIcon className="w-5 h-5 mr-2" />
                Payment Method
              </h2>

              <div className="space-y-3">
                <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="payment"
                    value="cash_on_delivery"
                    checked={paymentMethod === 'cash_on_delivery'}
                    onChange={(e) => setPaymentMethod(e.target.value as 'cash_on_delivery' | 'card')}
                    className="mr-3"
                  />
                  <BanknotesIcon className="w-6 h-6 text-green-500 mr-3" />
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Cash on Delivery</h3>
                    <p className="text-sm text-gray-600">
                      Pay with cash when your order arrives
                    </p>
                  </div>
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    Recommended
                  </span>
                </label>

                <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors opacity-50">
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    disabled
                    className="mr-3"
                  />
                  <CreditCardIcon className="w-6 h-6 text-gray-400 mr-3" />
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-500">Credit/Debit Card</h3>
                    <p className="text-sm text-gray-400">
                      Coming soon - Online payments
                    </p>
                  </div>
                  <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full">
                    Soon
                  </span>
                </label>
              </div>

              {paymentMethod === 'cash_on_delivery' && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-2">💵 Cash on Delivery</h4>
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>• Pay exact amount or we'll provide change</li>
                    <li>• Please have cash ready for quick delivery</li>
                    <li>• No extra charges for COD</li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <ShoppingBagIcon className="w-5 h-5 mr-2" />
                Your Order ({items.length})
              </h2>

              <div className="space-y-3 mb-4">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{item.itemName}</h4>
                      <p className="text-sm text-gray-600">
                        {item.quantity}x ${item.rate}
                      </p>
                      {item.customizations && item.customizations.length > 0 && (
                        <p className="text-xs text-blue-600">
                          {item.customizations.join(', ')}
                        </p>
                      )}
                    </div>
                    <span className="font-semibold text-gray-900">
                      ${item.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax (8%):</span>
                  <span>${taxes.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Delivery:</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="flex justify-between font-semibold text-lg pt-2 border-t border-gray-200">
                  <span>Total:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Delivery Time */}
            <div className="bg-primary-50 rounded-lg p-4">
              <h3 className="font-medium text-primary-900 mb-2">⏰ Estimated Delivery</h3>
              <p className="text-primary-800">
                {deliveryTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-sm text-primary-700">
                Usually delivered in 30-45 minutes
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              form="checkout-form"
              disabled={isLoading || items.length === 0}
              onClick={handleSubmitOrder}
              className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Placing Order...
                </span>
              ) : (
                `Place Order - $${total.toFixed(2)}`
              )}
            </button>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
                <p className="font-semibold mb-1">⚠️ Order Failed</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            <p className="text-xs text-gray-500 text-center">
              By placing this order, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;